import { useState, useEffect } from "react";
import styles from "./Footer.module.css";
import LocationIcon from "../../assets/locationIcon.jsx";
import LinkedInIcon from "../../assets/socials/LinkedIn.jsx";
import FacebookIcon from "../../assets/socials/facebook.jsx";
import InstagramIcon from "../../assets/socials/instagram.jsx";
import VimeoIcon from "../../assets/socials/Vimeo.jsx";
import YoutubeIcon from "../../assets/socials/YouTube.jsx";
import NetflixLogoFooter from "../../assets/netflixLogoFooter.jsx";
import XIcon from "../../assets/socials/x.jsx";
import { useParams } from "react-router-dom";
import Marquee from "react-fast-marquee";

// Data Items for Footer
const locations = [
    { city: "Los Angeles", city_ko: "로스앤젤레스", timezone: 'America/Los_Angeles', link: 'https://maps.app.goo.gl/tcakQ4k3zgiVX1958'},
    { city: "Vancouver", city_ko: "밴쿠버",  timezone: 'America/Vancouver', link: 'https://maps.app.goo.gl/sEcR8jWvecncpfW79' },
    { city: "Seoul", city_ko: "서울",  timezone: 'Asia/Seoul', link: 'https://maps.app.goo.gl/LaHhej16P17bUADV8' },
    { city: "Mumbai", city_ko: "뭄바이",  timezone: 'Asia/Kolkata', link: 'https://maps.app.goo.gl/ohQpF9MJtgR34Ko68' },
    { city: "Hyderabad", city_ko: "하이데라바드",  timezone: 'Asia/Kolkata', link: 'https://maps.app.goo.gl/noqUxgJ3nJPndkT88' },
    { city: "London", city_ko: "런던",  timezone: 'Europe/London', link: 'https://maps.app.goo.gl/2suDinXek6MzSsqM9' }
];

const links1 = [
    { label: "Studios", label_ko: "스튜디오", to: "/studios" },
    { label: "VFX", label_ko: "VFX", to: "/vfx" },
    { label: "Labs", label_ko: "연구소", to: "/labs" },
    { label: "About", label_ko: "소개", to: "/about"},
    { label: "Careers", label_ko: "채용", to: "/about/careers" }
];

const links2 = [
    { label: "Contact Us", label_ko: "문의", to: "/contact" }
];

const legalLinks = [
    { label: "Privacy", label_ko: "개인정보 처리방침", href: "https://help.netflix.com/legal/corporateprivacy" },
    { label: "Terms of Use", label_ko: "이용약관", href: "https://help.netflix.com/legal/eyelinestudiostou" }
];

// Helper function to get the time and GMT offset for a given timezone
function getTimeInTimezone(timeZone) {
    try {
        const now = new Date();

        // Format the time only for the specified timezone
        const timeInZone = now.toLocaleTimeString('en-US', {
            timeZone: timeZone,
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });

        // Get the GMT offset string (e.g., "GMT-07:00")
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timeZone,
            timeZoneName: 'longOffset'
        });
        const parts = formatter.formatToParts(now);
        const offsetPart = parts.find(part => part.type === 'timeZoneName');
        const gmtOffsetString = offsetPart ? offsetPart.value : 'Offset not available';

        // Use a regular expression to capture the sign and the hours
        let gmtOffset = gmtOffsetString;
        const match = gmtOffsetString.match(/GMT([+-])(\d+)/);
        if (match) {
            const sign = match[1];
            const hours = parseInt(match[2], 10);
            gmtOffset = `${sign}${hours}`;
        }

        return {
            time: timeInZone,
            gmtOffset: gmtOffset
        };
    } catch (error) {
        return {
            error: `Invalid timezone: "${timeZone}". Please provide a valid IANA timezone name (e.g., 'America/New_York').`
        };
    }
}


export default function Footer() {
    const { lang } = useParams();
    const [hoveredTimezone, setHoveredTimezone] = useState(null);
    const [currentTime, setCurrentTime] = useState({});

    // Effect to update time on hover
    useEffect(() => {
        let interval;
        if (hoveredTimezone) {
            interval = setInterval(() => {
                setCurrentTime(prevTime => ({
                    ...prevTime,
                    [hoveredTimezone]: getTimeInTimezone(hoveredTimezone),
                }));
            }, 1000); // Update every second
        }
        return () => clearInterval(interval);
    }, [hoveredTimezone]);

    const handleMouseEnter = (timezone) => {
        setHoveredTimezone(timezone);
        // Immediately update the time for the hovered location
        setCurrentTime(prevTime => ({
            ...prevTime,
            [timezone]: getTimeInTimezone(timezone),
        }));
    };

    const handleMouseLeave = () => {
        setHoveredTimezone(null);
    };


    // Only render marquee on mobile
    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)').matches : false
    );

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const mq = window.matchMedia('(max-width: 768px)');
        const onChange = (e) => setIsMobile(e.matches);
        if (mq.addEventListener) mq.addEventListener('change', onChange);
        else mq.addListener(onChange);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', onChange);
            else mq.removeListener(onChange);
        };
    }, []);

    return (
        <footer className={styles.footer}>
            <div className={styles.section}>
                <div className={styles.inner}>
                    <ul className={`${styles.locations} ${styles.desktop}`}>
                        {locations.map(location => {
                            const locationTime = currentTime[location.timezone] || getTimeInTimezone(location.timezone);
                            return (
                                <li
                                    key={location.city}
                                    onMouseEnter={() => handleMouseEnter(location.timezone)}
                                    onMouseLeave={handleMouseLeave}
                                >
                                    <a href={location.link} target="_blank">
                                        <div className={styles.contents}>
                                            <LocationIcon />
                                            <span className={styles.label}>{lang === "ko" ? location.city_ko : location.city}</span>
                                            <div className={styles.hover}>
                                                <span className={styles.time}>{locationTime.time}</span>
                                                <span className={styles.caption}>{lang === "ko" ? "시간" : "Time in"} {lang === "ko" ? location.city_ko : location.city}</span>
                                            </div>
                                        </div>
                                    </a>
                                </li>
                            );
                        })}
                    </ul>


                    {/* Only render this marquee on mobile */}

                    {isMobile && (<div className={styles.locations_mobile}>
                        <ul className={styles.locations}>
                            <Marquee loop={3} direction="left">
                                {Array.from({ length: 3 }).map((_, i) =>
                                    locations.map(location => (
                                        <span key={`${location.city}-${i}`}>
                                            <li>
                                                <div className={styles.contents}>
                                                    <LocationIcon />
                                                    <span className={styles.label}>{location.city}</span>
                                                </div>
                                            </li>
                                        </span>
                                    ))
                                )}
                            </Marquee>
                        </ul>
                    </div>)}



                </div>
            </div>

            <div className={styles.section}>
                <div className={styles.inner}>
                    <ul>
                        {links1.map(link => (
                            <li key={link.label}>
                                <a href={`/${lang}${link.to}`}>{lang === "ko" ? link.label_ko : link.label}</a>
                            </li>
                        ))}
                    </ul>
                    <ul>
                        {links2.map(link => (
                            <li key={link.label}>
                                <a href={`/${lang}${link.to}`}>{lang === "ko" ? link.label_ko : link.label}</a>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className={styles.section}>
                <div className={styles.inner}>
                    <div className={styles.socials}>
                        <ul>
                            <li><a href="https://www.facebook.com/eyelinestudios/" target="_blank"><FacebookIcon /></a></li>
                            <li><a href="https://x.com/eyelinestudios" target="_blank"><XIcon /></a></li>
                            <li><a href="https://www.instagram.com/eyelinestudios/" target="_blank"><InstagramIcon /></a></li>
                            <li><a href="https://www.linkedin.com/company/eyelinestudios/" target="_blank"><LinkedInIcon /></a></li>
                            <li><a href="https://vimeo.com/eyelinestudios" target="_blank"><VimeoIcon /></a></li>
                            <li><a href="https://www.youtube.com/@eyelinestudios" target="_blank"><YoutubeIcon /></a></li>
                        </ul>
                    </div>
                    <div className={styles.legalCredits}>
                        <div className={styles.legal}>
                            <ul>
                                {legalLinks.map(link => (
                                    <li key={link.label}>
                                        <a href={link.href} target="_blank" rel="noopener noreferrer">
                                            {lang === "ko" ? link.label_ko : link.label}
                                        </a>
                                    </li>
                                ))}
                                <li>
                                    <button 
                                        type="button" 
                                        onClick={() => window.OneTrust?.ToggleInfoDisplay()}
                                        className={styles.cookiePrefsBtn}
                                    >
                                        {lang === "ko" ? "쿠키 설정" : "Cookie Preferences"}
                                    </button>
                                </li>
                            </ul>
                        </div>
                        <div className={styles.credits}>
                            {lang === "ko" ? (
                                <p>&copy; {new Date().getFullYear()} Eyeline. 모든 권리 보유. 사이트 기준 <a href="https://www.eastofwestern.com">East of Western.</a></p>
                            ) : (
                                <p>&copy; {new Date().getFullYear()} Eyeline. All rights reserved. Site by <a href="https://www.eastofwestern.com">East of Western.</a></p>
                            )}
                        </div>
                    </div>
                    <div className={styles.netflix}>
                        <p><a href="https://about.netflix.com" target="_blank"><NetflixLogoFooter /></a></p>
                    </div>
                </div>
            </div>
        </footer>
    );
}
